import { Fragment, Node as PMNode } from "prosemirror-model";
import { Plugin } from "prosemirror-state";

import schema from "../schema";

/**
 * Composer-specific behavior:
 * - Shift+Enter inserts a hard line break (stays within a single paragraph)
 * - Normalize the document to a single paragraph (no "blocks")
 */
export function composerPlugin(): Plugin {
  return new Plugin({
    props: {
      handleKeyDown(view, event) {
        if (event.key !== "Enter" || event.isComposing) return false;

        // Shift+Enter inserts a hard_break node in the composer.
        // We use a real `hard_break` (rendered as <br>) rather than a literal
        // "\n" text character: the latter is brittle inside contenteditable
        // and causes the caret/text to snap back to the previous line as
        // soon as more characters are typed.
        if (event.shiftKey) {
          event.preventDefault();
          view.dispatch(
            view.state.tr
              .replaceSelectionWith(schema.nodes.hard_break.create())
              .scrollIntoView()
          );
          return true;
        }

        // Note: suggestion plugins run before this composer plugin, so when a
        // suggestion popup is active, it can intercept Enter and we won't emit.
        try {
          view.dom.dispatchEvent(
            new CustomEvent("composer-submit", { bubbles: true })
          );
        } catch {
          // ignore
        }

        // Return true to prevent ProseMirror default behavior from inserting blocks.
        event.preventDefault();
        return true;
      },
    },
    appendTransaction(_trs, _oldState, newState) {
      const doc = newState.doc;

      // Ensure the document always has exactly one paragraph at the top level.
      if (
        doc.childCount === 1 &&
        doc.firstChild?.type === schema.nodes.paragraph
      )
        return null;

      const parts: PMNode[] = [];
      doc.forEach((child, _offset, index) => {
        if (index > 0) parts.push(schema.nodes.hard_break.create());
        if (child.type === schema.nodes.paragraph) {
          child.content.forEach((n) => parts.push(n));
        } else if (child.isTextblock) {
          child.content.forEach((n) => parts.push(n));
        } else {
          // Fallback: keep readable text.
          const text = child.textContent || "";
          if (text) parts.push(schema.text(text));
        }
      });

      const paragraph = schema.nodes.paragraph.create(
        null,
        Fragment.fromArray(parts)
      );
      const nextDoc = schema.nodes.doc.create(null, [paragraph]);

      // Avoid infinite loops.
      if (nextDoc.eq(doc)) return null;

      const tr = newState.tr.replaceWith(0, doc.content.size, nextDoc.content);
      tr.setMeta("addToHistory", false);
      return tr;
    },
  });
}
